// Libraries
import { type CommonDataLayerV3Props } from '@makemydeal/dr-common-data-layer-utils';
import { HostEnvironment, hostUrlHelper, locHrefUtil, SALES_CHECKIN_APP_PREFIX } from '@makemydeal/dr-shared-ui-utils';
import { SALES_CHECK_IN_APPLICATION_NAME } from '@makemydeal/dr-shared-types';

/**
 * Builds CDL V3 object from Redux state.
 * This function reads deal and client data from state if available,
 * ensuring CDL is initialized with the most up-to-date information.
 *
 * @param state - Redux state object
 * @returns CDL V3 object with application, user, client, deal, and vehicle sections
 */
export const buildCdlV3ObjectFromState = (state: any): CommonDataLayerV3Props => {
    const environmentEnum = hostUrlHelper.getHostUrlEnvironment(SALES_CHECKIN_APP_PREFIX, locHrefUtil.getLocHref());
    const deal = state.salesCheckin?.activeDeal?.details;
    const currentDealership = state.dealer;
    // Bridge user info read directly from state to keep this util store-free
    // (importing dash-store selectors here would create a ui-utils -> store cycle).
    const bridgeUser = state.bridgeUser || {};

    // A conditional spread (`...(x && { ... })`) opts the spread-in properties out of TypeScript's
    // excess-property checking, so contract violations inside it go unreported. Building each
    // section as a literal annotated with its contract type keeps it checked. Behavior is
    // unchanged: a falsy source still yields an empty section.
    const dealSection: CommonDataLayerV3Props['deal'] = deal
        ? {
              dealId: deal.dealId || '',
              dealVersion: deal.dealStructure?.version || '',
              dealStatus: deal.dealLifecycle?.status || '',
              commonConsumerId: deal.dealStructure?.participants?.buyer?.commonConsumerId || ''
          }
        : {};

    /**
     * This type allows fields that are not actually defined in dr-common-data-layer-utils.  They have historically been used in
     * Sales Check-In so they are allowed to preserve the legacy runtime behavior, but are probably incorrect.  However, if they
     * are correct they will be added to dr-common-data-layer-utils and this AdditionalIncompatibleFields type will be dropped.
     */
    type AdditionalIncompatibleFields = {
        type?: string; // this may be valid, but is probably not sent by our other apps
    };
    const clientSection: CommonDataLayerV3Props['client'] & AdditionalIncompatibleFields = currentDealership
        ? {
              name: currentDealership.dealerName || '',
              commonOrgId: currentDealership.commonOrgId || '',
              mmdId: currentDealership.dealerId || '', // redundant and matches current contract so it is favored
              vinsolutionsId: currentDealership.dealerId || '',
              isTestDealer: currentDealership.isTestDealer || false,
              type: 'dealer'
          }
        : {};

    const cdlV3Data: CommonDataLayerV3Props = {
        application: {
            componentId: 'CI100652282',
            isProduction: environmentEnum === HostEnvironment.Prod,
            name: SALES_CHECK_IN_APPLICATION_NAME,
            workloadId: 'CI0433779'
        },
        user: {
            bridgePlatformId: bridgeUser.id || '',
            bridgeUsername: bridgeUser.username || '',
            isInternalUser: false,
            type: 'dealer'
        },
        deal: dealSection,
        client: clientSection,
        vehicle: {}
    };
    return cdlV3Data;
};
